import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { KutroLogoFull } from './Logo';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

// ─── Consolidated nav structure (5 top-level items instead of 14 flat) ─────
const navGroups = [
  {
    label: 'KYPA',
    testId: 'nav-group-kypa',
    badge: 'NEW',
    items: [
      { name: 'What is KYPA?', path: '/kypa', desc: 'Overview of the pledged-agent registry' },
      { name: 'The Instrument', path: '/kypa/instrument', desc: 'KYPA Credential Envelope · merchant integration spec', badge: 'NEW' },
      { name: 'Browse Directory', path: '/kypa/directory', desc: 'Search all enrolled agents' },
      { name: 'Binding Action Gate', path: '/binding-gate', desc: 'Proposed design, not in service' },
      { name: 'Open Consent Receipt Spec', path: '/specs/consent-receipt', desc: 'Free open format · operators run it themselves', badge: 'DRAFT' },
      { name: 'Claim Records', path: '/specs/claim-records', desc: 'Refunds and complaints raised by agents · proposal', badge: 'PROPOSAL' },
      { name: 'Agent-Ready File', path: '/specs/agent-ready/demo', desc: 'Five promises agents can check · concept for comment', badge: 'DRAFT' },
      { name: 'Featured Profile', path: '/kypa/agent/KYPA-aaae9a-13a7', desc: 'See a live agent profile · pledge · breach records' },
      { name: 'Enroll Your Agent', path: '/register-agent?mode=kypa', desc: 'Pledge USDC · get listed', badge: 'START HERE' },
      { name: 'Compare vs. Credit Bureau', path: '/vs/experian-agent-trust', desc: 'Kojiru KYPA vs. a credit bureau agent trust product · side-by-side' },
    ],
  },
  {
    label: 'Platform',
    testId: 'nav-group-platform',
    items: [
      { name: 'Breach Proofs', path: '/proofs', desc: 'Every on-chain breach record · independently verifiable', badge: 'LIVE LEDGER' },
      { name: 'Agent Credit', path: '/agent-credit', desc: 'Credit lines & ACS™ scoring' },
      { name: 'AaaS', path: '/aaas', desc: 'Agent-as-a-Service subscriptions' },
      { name: 'Staking', path: '/staking', desc: 'Stake KOJI · earn rewards' },
      { name: 'Liability Map', path: '/adl-m', desc: 'Agent-Directory Liability Map' },
    ],
  },
  {
    label: 'Developers',
    testId: 'nav-group-developers',
    items: [
      { name: 'MCP Integration', path: '/mcp', desc: 'Native MCP server · npm · PyPI' },
      { name: 'Developer Docs', path: '/developers', desc: 'API reference & guides' },
      { name: 'AI Tools', path: '/agents', desc: 'Live agent dashboard' },
      { name: 'Tokenomics', path: '/tokenomics', desc: 'KOJI supply & distribution' },
    ],
  },
  {
    label: 'Audiences',
    testId: 'nav-group-audiences',
    badge: 'NEW',
    items: [
      { name: 'For Issuing Programs', path: '/for-issuers', desc: 'BIN sponsors · policy check inside the issuer authorization callback · two-second window', badge: 'NEW' },
      { name: 'For Underwriters', path: '/for-underwriters', desc: 'Cyber · E&O · agent-liability · uninsurable → insurable via pledge + optional review', badge: 'NEW' },
      { name: 'For Merchants', path: '/for-merchants', desc: 'Accept agent traffic safely · KYPA verify at checkout', badge: 'NEW' },
      { name: 'For B2B Procurement', path: '/for-b2b-procurement', desc: 'Agent-issued POs · invoicing · reconciliation · supplier onboarding · the first proving ground', badge: 'NEW' },
      { name: 'For Banks', path: '/for-banks', desc: 'Bank product leaders · trust parity + evidence layer', badge: 'NEW' },
      { name: 'For Payments Platforms', path: '/for-payments-platforms', desc: 'PayFacs · ISOs · processors · embed recourse' },
    ],
  },
  {
    label: 'Company',
    testId: 'nav-group-company',
    items: [
      { name: 'Readiness Self-Audit', path: '/readiness', desc: 'The five-question readiness test · re-audited Feb 2026 · one passed at n=1, three still open', badge: 'HONEST' },
      { name: 'Operations Playbook', path: '/ops', desc: 'Who monitors · who responds · what we owe you' },
      { name: 'Three Analysts. One Gap.', path: '/three-analysts-one-gap', desc: 'Four independent industry analysts, overlaid', badge: 'NEW' },
      { name: 'Agent Insurance Questionnaire', path: '/vs/agent-insurance-questionnaire', desc: "The underwriter's five questions · Kojiru's on-chain answers", badge: 'NEW' },
      { name: 'Frontier Lab Commerce Agents', path: '/vs/claude-commerce-agents', desc: "A frontier AI lab's five-stage flow · KYPA completes stage 5", badge: 'NEW' },
      { name: 'Seven Conditions Response', path: '/seven-conditions', desc: 'Our response to a digital-money podcast · Ep. 1' },
      { name: 'KYA Six-Layer Mapping', path: '/kya', desc: 'Know-Your-Agent framework, mapped layer by layer', badge: 'COMPLIANCE' },
      { name: 'About', path: '/about', desc: 'Our story & mission' },
      { name: 'Partners', path: '/partners', desc: 'Institutional alliances' },
      { name: 'Blog', path: '/blog', desc: 'Product & research posts' },
      { name: 'Compliance', path: '/compliance', desc: 'For risk & compliance teams' },
      { name: 'FAQ', path: '/faq', desc: 'Common questions' },
    ],
  },
];

const flatLinks = [
  { name: 'Manifesto', path: '/manifesto', testId: 'nav-link-manifesto', badge: 'NEW' },
  { name: 'Contact', path: '/contact', testId: 'nav-link-contact' },
];

// ─── Hover-triggered dropdown ─────────────────────────────────────────────
function NavDropdown({ group }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={group.testId}
    >
      <button
        type="button"
        className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors py-2"
      >
        {group.label}
        {group.badge && (
          <span className="text-[8px] font-bold uppercase tracking-widest px-1 py-px rounded bg-red-500/20 text-red-300 border border-red-500/30 leading-none">
            {group.badge}
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50"
          >
            <div
              className="min-w-[300px] rounded-xl bg-[#0a0a0a] border border-white/10 shadow-2xl shadow-black/50 p-2"
              data-testid={`${group.testId}-panel`}
            >
              {group.items.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="block px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors group"
                  data-testid={`nav-item-${item.path.replace(/\//g, '')}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white group-hover:text-red-300 transition-colors">
                      {item.name}
                    </span>
                    {item.badge && (
                      <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/30">
                        {item.badge}
                      </span>
                    )}
                  </div>
                  {item.desc && (
                    <p className="text-xs text-gray-500 mt-0.5 leading-snug">{item.desc}</p>
                  )}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5"
      data-testid="navbar"
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo">
            <KutroLogoFull size={36} />
          </Link>

          {/* Desktop Nav: 5 items only */}
          <div className="hidden md:flex items-center gap-8">
            {navGroups.map((group) => (
              <NavDropdown key={group.label} group={group} />
            ))}
            {flatLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-sm text-gray-400 hover:text-white transition-colors py-2 inline-flex items-center gap-1.5"
                data-testid={link.testId}
              >
                {link.name}
                {link.badge && (
                  <span className="text-[8px] font-bold uppercase tracking-widest px-1 py-px rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 leading-none">
                    {link.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/auth" data-testid="navbar-sign-in">
              <Button variant="ghost" className="text-sm">
                Sign In
              </Button>
            </Link>
            <Link to="/auth?role=consumer" data-testid="navbar-get-started">
              <Button className="text-sm bg-gradient-to-r from-blue-500 to-purple-500 hover:opacity-90">
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="navbar-mobile-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav: accordion */}
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden py-4 border-t border-white/5"
            data-testid="navbar-mobile-panel"
          >
            {navGroups.map((group) => (
              <div key={group.label} className="mb-4">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1 px-1">
                  {group.label}
                </div>
                {group.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-2 py-2 px-1 text-gray-300 hover:text-white"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.name}
                    {item.badge && (
                      <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/30">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            ))}
            {flatLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="block py-2 px-1 text-gray-300 hover:text-white"
                onClick={() => setMobileOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="flex gap-3 mt-4">
              <Link to="/auth" className="flex-1">
                <Button variant="outline" className="w-full">
                  Sign In
                </Button>
              </Link>
              <Link to="/auth?role=consumer" className="flex-1">
                <Button className="w-full bg-gradient-to-r from-blue-500 to-purple-500">
                  Get Started
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#050505] border-t border-white/5 py-16">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <KutroLogoFull size={40} />
            <p className="text-gray-400 mt-4 max-w-xs">
              Credit Infrastructure for AI Agents. The Agent Credit Protocol built on Base.
            </p>
          </div>

          {/* Product */}
          <div>
            <h4 className="font-semibold text-white mb-4">Product</h4>
            <ul className="space-y-2 text-gray-400">
              <li><Link to="/manifesto" className="hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="footer-manifesto-link">The Recourse Manifesto <span className="text-[8px] font-bold uppercase tracking-widest px-1 py-px rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 leading-none">NEW</span></Link></li>
              <li><Link to="/how-a-dispute-works" className="hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="footer-how-dispute-works-link">How a Dispute Works <span className="text-[8px] font-bold uppercase tracking-widest px-1 py-px rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 leading-none">NEW</span></Link></li>
              <li><Link to="/kypa" className="hover:text-white transition-colors">What is KYPA?</Link></li>
              <li><Link to="/kypa/instrument" className="hover:text-white transition-colors">The Instrument</Link></li>
              <li><Link to="/kypa/directory" className="hover:text-white transition-colors">Browse Directory</Link></li>
              <li><Link to="/binding-gate" className="hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="footer-binding-gate-link">Binding Action Gate (proposed)</Link></li>
              <li><Link to="/specs/consent-receipt" className="hover:text-white transition-colors" data-testid="footer-open-spec-link">Open Consent Receipt Spec</Link></li>
              <li><Link to="/specs/claim-records" className="hover:text-white transition-colors" data-testid="footer-claim-records-link">Claim Records</Link></li>
              <li><Link to="/register-agent?mode=kypa" className="hover:text-white transition-colors">Enroll Your Agent</Link></li>
              <li><Link to="/how-it-works" className="hover:text-white transition-colors">How It Works</Link></li>
              <li><Link to="/litepaper" className="hover:text-white transition-colors">Litepaper</Link></li>
              <li><Link to="/tokenomics" className="hover:text-white transition-colors">Tokenomics</Link></li>
              <li><Link to="/analytics" className="hover:text-white transition-colors">Analytics</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-white mb-4">Company</h4>
            <ul className="space-y-2 text-gray-400">
              <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
              <li><Link to="/research" className="hover:text-white transition-colors">Research</Link></li>
              <li><Link to="/partners" className="hover:text-white transition-colors">Partners</Link></li>
              <li><Link to="/compliance" className="hover:text-white transition-colors" data-testid="footer-compliance-link">For Risk &amp; Compliance</Link></li>
              <li><Link to="/vs/experian-agent-trust" className="hover:text-white transition-colors" data-testid="footer-vs-experian-link">vs. Credit Bureau Agent Trust</Link></li>
              <li><Link to="/kya" className="hover:text-white transition-colors" data-testid="footer-kya-link">KYA Six-Layer Mapping</Link></li>
              <li><Link to="/three-analysts-one-gap" className="hover:text-white transition-colors" data-testid="footer-three-analysts-link">Three Analysts. One Gap.</Link></li>
              <li><Link to="/for-payments-platforms" className="hover:text-white transition-colors" data-testid="footer-for-payments-platforms-link">For Payments Platforms</Link></li>
              <li><Link to="/proofs" className="hover:text-white transition-colors" data-testid="footer-proofs-link">Breach Proofs</Link></li>
              <li><Link to="/readiness" className="hover:text-white transition-colors" data-testid="footer-readiness-link">Readiness Self-Audit</Link></li>
              <li><Link to="/ops" className="hover:text-white transition-colors" data-testid="footer-ops-link">Operations Playbook</Link></li>
              <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold text-white mb-4">Support</h4>
            <ul className="space-y-2 text-gray-400">
              <li><Link to="/resources" className="hover:text-white transition-colors">Resources</Link></li>
              <li>
                <a
                  href="https://credit.kojiru.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors inline-flex items-center gap-1.5"
                  data-testid="footer-live-dashboard-link"
                >
                  <span className="relative flex w-1.5 h-1.5">
                    <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-60 animate-ping"></span>
                    <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                  </span>
                  Live Dashboard
                </a>
              </li>
              <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              <li><Link to="/support" className="hover:text-white transition-colors">Help Center</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © 2026 Kojiru. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/admin" className="hover:text-white transition-colors text-gray-400">Admin</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/security" className="hover:text-white transition-colors" data-testid="footer-security-link">Security</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
